import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';

import { useNectarImpression } from '~/hooks/useNectarImpression';
import type { loader as communityLoader } from '~/routes/community';
import { NectarPageBlock, nectarPageBlock } from '~/utils/nectarPageBlock';

import { PillButton } from './ui/PillButton';
import { SignedInAs } from './ui/SignedInAs';

import { useCommunityDisplayName } from '../_hooks/useCommunityDisplayName';
import { useCommunityMembership } from '../_hooks/useCommunityMembership';
import { getJoinRefusalMessage } from '../_utils/getJoinRefusalMessage';
import { GateBannerCard, GateBannerLogo, GateBannerNote, GateBannerTitle } from './GateBannerCard';
import { OwnerGateButton } from './OwnerGateButton';

interface JoinRefusedBannerProps {
  onJoin?: () => void;
}

export const JoinRefusedBanner: FC<JoinRefusedBannerProps> = ({ onJoin }) => {
  const { t } = useTranslation();
  const community = useRouteLoaderData<typeof communityLoader>('routes/community')?.community;
  const communityName = useCommunityDisplayName();
  const { isMember } = useCommunityMembership();
  useNectarImpression({ pageBlock: NectarPageBlock.CommunityJoinRefused }, { enabled: !onJoin });

  return (
    <GateBannerCard>
      <GateBannerLogo alt={communityName} />
      <GateBannerTitle>{communityName}</GateBannerTitle>
      <p className="mt-3 max-w-[380px] whitespace-pre-line text-base leading-6 text-community-text-on-bg/75">
        {getJoinRefusalMessage(community, t)}
      </p>
      {isMember && <GateBannerNote>{t('community.join_refused.access_changed')}</GateBannerNote>}
      {onJoin ? (
        <PillButton
          type="button"
          onClick={onJoin}
          variant="primary"
          size="lg"
          className="mt-7 min-w-[168px]"
          {...nectarPageBlock(NectarPageBlock.CommunityJoinBanner)}
        >
          {t('community.join.cta')}
        </PillButton>
      ) : (
        <OwnerGateButton gate={community?.gate} size="lg" className="mt-7 min-w-[168px]" />
      )}
      <SignedInAs className="mt-5 text-community-text-on-bg/50" />
    </GateBannerCard>
  );
};
